import { Card, CardHeader } from "@/components/Card";
import type { Titular } from "@/lib/types";

function initials(t: Titular) {
  const parts = [t.nombre, t.primerApellido].filter(Boolean);
  return parts.map((p) => p[0] ?? "").join("").slice(0, 2).toUpperCase();
}

const AVATAR_TONES = [
  "bg-indigo-100 text-indigo-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
];

export function TitularesCard({ titulares }: { titulares: Titular[] }) {
  return (
    <Card>
      <CardHeader
        title={`Titulares (${titulares.length})`}
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
            aria-hidden
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        }
      />

      {titulares.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">
          Este folio no tiene titulares registrados.
        </p>
      ) : (
        <ul className="mt-4 space-y-4">
          {titulares.map((t, i) => {
            const ddMatch = t.porcentajeDdUv.match(/(\d+(?:\.\d+)?)/);
            const pct = ddMatch ? Math.min(100, Number(ddMatch[1])) : 0;
            const fullName = [t.nombre, t.primerApellido, t.segundoApellido]
              .filter(Boolean)
              .join(" ");
            return (
              <li
                key={`${t.nombre}-${i}`}
                className="flex items-start gap-4 rounded-xl border border-slate-100 bg-slate-50/50 p-4"
              >
                <span
                  className={`grid h-11 w-11 shrink-0 place-items-center rounded-full text-sm font-semibold ${AVATAR_TONES[i % AVATAR_TONES.length]}`}
                  aria-hidden
                >
                  {initials(t)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900">{fullName}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Nac./RFC{" "}
                    <span className="font-mono text-slate-700">
                      {t.fechaNacimientoRfc}
                    </span>
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <div
                      className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200"
                      role="progressbar"
                      aria-valuenow={pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className="h-full rounded-full bg-indigo-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs font-semibold tabular-nums text-slate-700">
                      {t.porcentajeDdUv}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
